import axios from 'axios';
import type { Clip } from './assemblyClip';

// Publishing sends a metadata *snapshot* to the backend, because a manual's
// title / brand / step labels otherwise live only in the browser (IndexedDB +
// the module stores). The backend keys everything by docId.
// One sub-step of a step: its lettered key (a, b, c…; a single-action step is keyed "a")
// and its instruction text in both languages, so the public page can follow its FR/EN
// toggle.
export type PublishSubstep = {
  key: string | null;
  textEn: string;
  textFr: string;
};
export type PublishStep = { step: number | null; label: string; substeps: PublishSubstep[] };

// Splash-screen metadata for the public page's first screen.
export type PublishInfo = {
  name: string; // the product's own name (e.g. "BILLY"), preferred headline
  category: string; // product category (e.g. "Chair"), headline fallback
  time: string; // estimated assembly time, as detected (e.g. "20 minutes")
  // The finished product, cropped from the manual as a PNG data URL — the splash's
  // hero image. Empty when the manual pictures no finished product; the backend
  // stores the bytes and mirrors this field, so publishing without one clears it.
  heroImage: string;
};

// One part on the public page's Tools or Parts screen (see usePublishParts).
export type PublishPart = {
  nameEn: string;
  nameFr: string;
  qty: number | null;
  // The manual's letter for it ("C"), empty where it prints none.
  letter: string;
  tool: boolean;
  // Its drawing, cut from the manual, as a PNG data URL; empty where it has none.
  image: string;
  // Its 3D model, where the Parts DB has one (see usePublishParts).
  job: number | null;
  // Whether it is in the box (see PartItem.supplied); the Tools screen says so.
  supplied: boolean;
};

export type PublishSnapshot = {
  docId: string;
  title: string;
  brand: string;
  steps: PublishStep[];
  info: PublishInfo;
  // THE ANIMATIONS, keyed by screen ("1A", "2B") — one per sub-step Phase 5 has signed off.
  // A movement and the meshes it moves, not a video: see assemblyClip. Screens with no entry
  // keep the empty frame, which is most of them on most manuals.
  clips: Record<string, Clip>;
  // THE TOOLS AND PARTS, shown before the first step.
  parts: PublishPart[];
};

export type PublishStatus = {
  published: boolean;
  url: string | null;
  // When the page was last (re)published, ISO. Null when never published.
  updatedAt: string | null;
  // The run the live page was made from, or null when it is not known — a page published
  // before this was recorded. Unknown is not "all of them".
  runId: string | null;
};

export async function fetchPublishStatus(docId: string): Promise<PublishStatus> {
  const { data } = await axios.get<PublishStatus>('/api/notice/publish/status', {
    params: { docId },
  });
  return data;
}

// `runId` is read at the moment of publishing rather than carried in the snapshot: the
// snapshot is memoised from the answers, and the run on screen is not one of them. The
// server records it so the app can later tell the run the live page was made from apart
// from the twelve it was not.
export async function publishTutorial(
  snapshot: PublishSnapshot,
  runId: string,
): Promise<{ url: string }> {
  const { data } = await axios.post<{ url: string }>('/api/notice/publish', {
    ...snapshot,
    runId,
  });
  return data;
}

export async function unpublishTutorial(docId: string): Promise<void> {
  await axios.post('/api/notice/unpublish', { docId });
}
